import React from 'react';
import { Checkmark16Regular, Dismiss16Regular, Edit12Regular } from '@fluentui/react-icons';
import styles from './irlObservations2026.module.css';
import searchIcon from './assets/search.svg';
import { IRL_HEADER_STATS, countIrlNav, countObsNav, WORKSTREAMS } from './dummyData';

const WORKFLOW_STATUS_LABELS = {
    DRAFT: 'Draft',
    OPEN: 'Open',
    IN_PROGRESS: 'In progress',
    RESPONDED: 'Responded',
    NEEDS_REVIEW: 'Needs review',
    CLOSED: 'Closed',
    NOT_APPLICABLE: 'Not applicable',
    CANNOT_PROVIDE: 'Cannot provide',
};

const CLOSED_WORKFLOW_STATUSES = new Set(['CLOSED', 'NOT_APPLICABLE', 'CANNOT_PROVIDE']);
const CLOSED_STATUS_LABELS = new Set(['Closed', 'Not applicable', 'Cannot provide']);

const IRL_STATUS_CLASS = {
    Responded: styles.statusResponded,
    Open: styles.statusOpen,
    'Needs review': styles.statusNeedsReview,
    'In progress': styles.statusInProgress,
    Closed: styles.statusClosed,
    Draft: styles.statusDraft,
    'Not applicable': styles.statusClosed,
    'Cannot provide': styles.statusClosed,
};

const OBS_STATUS_CLASS = {
    Confirmed: styles.statusConfirmed,
    Resolved: styles.statusResolved,
    Draft: styles.statusDraft,
    Superseded: styles.statusMuted,
    Archived: styles.statusMuted,
};

function startOfToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

function parseRequestDueDate(value) {
    if (!value || typeof value !== 'string') return null;
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!iso) return null;
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
}

export function requestStatusLabel(item) {
    if (item == null) return 'Open';
    const workflow = item.workflowStatus || item.workflow_status;
    if (workflow && WORKFLOW_STATUS_LABELS[workflow]) {
        return WORKFLOW_STATUS_LABELS[workflow];
    }
    const raw = item.status;
    if (typeof raw === 'string') {
        if (WORKFLOW_STATUS_LABELS[raw]) return WORKFLOW_STATUS_LABELS[raw];
        if (raw) return raw;
    }
    if (raw === true) return 'Closed';
    if (item.response) return 'Responded';
    return 'Open';
}

export function isOpenIrlLayout(item) {
    const label = requestStatusLabel(item);
    return label === 'Open' || label === 'In progress';
}

function canEditRequestField(onSave) {
    return typeof onSave === 'function';
}

export function isRequestOverdue(item) {
    if (!item) return false;
    const label = requestStatusLabel(item);
    const workflow = item.workflowStatus || item.workflow_status;
    if (CLOSED_WORKFLOW_STATUSES.has(workflow) || CLOSED_STATUS_LABELS.has(label)) {
        return false;
    }
    const due = parseRequestDueDate(item.dueDate || item.due_date);
    if (due) return due < startOfToday();
    return Boolean(item.dueOverdue);
}

export function ModuleShell({ children }) {
    return <div className={styles.page}>{children}</div>;
}

export function IrlModuleHeader({ stats }) {
    const open = stats?.open ?? IRL_HEADER_STATS.open;
    const needsReview = stats?.needsReview ?? IRL_HEADER_STATS.needsReview;
    const overdue = stats?.overdue ?? IRL_HEADER_STATS.overdue;
    return (
        <div className={styles.moduleHeader}>
            <h1 className={`${styles.moduleTitle} irl-text`}>IRL</h1>
            <div className={styles.moduleStats}>
                <span className={styles.statOpen}>{open} open</span>
                <span className={styles.statSep}> · </span>
                <span className={styles.statReview}>{needsReview} needs review</span>
                <span className={styles.statSep}> · </span>
                <span className={styles.statOverdue}>{overdue} overdue</span>
            </div>
        </div>
    );
}

export function ObsModuleHeader({ stats }) {
    const confirmed = stats?.confirmed ?? 0;
    const mayNeedReview = stats?.mayNeedReview ?? 0;
    const draft = stats?.draft ?? 0;
    return (
        <div className={styles.moduleHeader}>
            <h1 className={styles.moduleTitle}>Observations</h1>
            <div className={styles.moduleStats}>
                <span className={styles.statGreen}>{confirmed} confirmed</span>
                <span className={styles.statSep}> · </span>
                <span className={styles.statAmber}>{mayNeedReview} may need review</span>
                <span className={styles.statSep}> · </span>
                <span className={styles.statMuted}>{draft} draft</span>
            </div>
        </div>
    );
}

function irlStatusNavKey(status) {
    switch (status) {
        case 'All':
            return 'all';
        case 'Needs review':
            return 'needsReview';
        case 'Overdue':
            return 'overdue';
        case 'Open':
        case 'In progress':
        case 'Responded':
        case 'Closed':
            return null;
        default:
            return null;
    }
}

function obsStatusNavKey(status) {
    switch (status) {
        case 'All':
            return 'all';
        case 'Draft':
            return 'draft';
        case 'Resolved':
            return 'resolved';
        case 'May need review':
        case 'New evidence':
        case 'Review required':
            return 'mayNeedReview';
        case 'Confirmed':
        case 'Superseded':
            return null;
        default:
            return null;
    }
}

export function IrlNavTree({ status, onStatus, workstream, onWorkstream, requests }) {
    const counts = countIrlNav(requests);
    const needsReview = requests.filter(item => requestStatusLabel(item) === 'Needs review').length;
    const overdue = requests.filter(item => isRequestOverdue(item)).length;
    const activeStatus = irlStatusNavKey(status);
    return (
        <nav className={styles.sidebar}>
            <p className={styles.sectionLabel}>Information Requests</p>
            <NavButton active={activeStatus === 'all'} onClick={() => onStatus('All')} label="All Requests" count={counts.all} />
            <NavButton active={activeStatus === 'needsReview'} onClick={() => onStatus('Needs review')} label="Needs Review" count={needsReview} />
            <NavButton active={activeStatus === 'overdue'} onClick={() => onStatus('Overdue')} label="Overdue" count={overdue} />
            <p className={styles.sectionLabel}>Workstreams</p>
            {counts.workstreams.map(ws => (
                <NavButton
                    key={ws.id}
                    active={String(workstream) === String(ws.id)}
                    onClick={() => onWorkstream(String(ws.id))}
                    label={ws.name}
                    count={ws.count}
                />
            ))}
        </nav>
    );
}

export function ObsNavTree({ status, onStatus, workstream, onWorkstream, observations, workstreams }) {
    const counts = countObsNav(observations, workstreams);
    const activeStatus = obsStatusNavKey(status);
    return (
        <nav className={styles.sidebar}>
            <p className={styles.sectionLabel}>Observations</p>
            <NavButton active={activeStatus === 'all'} onClick={() => onStatus('All')} label="All Observations" count={counts.all} />
            <NavButton active={activeStatus === 'mayNeedReview'} onClick={() => onStatus('May need review')} label="May Need Review" count={counts.mayNeedReview} />
            <NavButton active={activeStatus === 'draft'} onClick={() => onStatus('Draft')} label="Draft" count={counts.draft} />
            <NavButton active={activeStatus === 'resolved'} onClick={() => onStatus('Resolved')} label="Resolved" count={counts.resolved} />
            <p className={styles.sectionLabel}>Workstreams</p>
            {counts.workstreams.map(ws => (
                <NavButton
                    key={ws.id}
                    active={String(workstream) === String(ws.id)}
                    onClick={() => onWorkstream(String(ws.id))}
                    label={ws.name}
                    count={ws.count}
                />
            ))}
        </nav>
    );
}

function NavButton({ active, onClick, label, count }) {
    return (
        <button type="button" className={`${styles.navItem} ${active ? styles.navItemActive : ''}`} onClick={onClick}>
            <span>{label}</span>
            <span className={styles.navCount}>{count}</span>
        </button>
    );
}

export function SearchField({ value, onChange, placeholder }) {
    return (
        <div className={styles.searchWrap}>
            <img src={searchIcon} alt="" className={styles.searchIcon} width={14} height={14} />
            <input className={styles.searchInput} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} />
        </div>
    );
}

export function FilterChip({ label, active, onClick }) {
    return (
        <button type="button" className={`${styles.chip} ${active ? styles.chipActive : ''}`} onClick={onClick}>
            {label}
        </button>
    );
}

export function StatusBadge({ status, appearance }) {
    const label = WORKFLOW_STATUS_LABELS[status] || status;
    const irlClass = IRL_STATUS_CLASS[label];
    if (irlClass) {
        return <span className={`${styles.statusLabel} ${irlClass}`}>{label}</span>;
    }
    const obsClass = OBS_STATUS_CLASS[label];
    if (appearance === 'text' && obsClass) {
        return <span className={`${styles.statusLabel} ${obsClass}`}>{label}</span>;
    }
    return <span className={`${styles.badge} ${observationStatusClass(label)}`}>{label}</span>;
}

function observationStatusClass(status) {
    switch (status) {
        case 'Confirmed':
            return styles.badgeGreen;
        case 'Review required':
            return styles.badgeAmber;
        case 'Resolved':
            return styles.badgeResolved;
        case 'Superseded':
        case 'Archived':
            return styles.badgeMuted;
        case 'New evidence':
        case 'Potentially affected':
            return styles.badgeNewEvidence;
        case 'Overdue':
            return styles.priorityHigh;
        default:
            return styles.badgeMuted;
    }
}

function OverdueMark() {
    return (
        <svg className={styles.dueOverdueIcon} width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M5.13 1.62a1 1 0 0 1 1.74 0l4.36 7.88A1 1 0 0 1 10.36 11H1.64a1 1 0 0 1-.87-1.5l4.36-7.88z" />
            <path fill="#fff" d="M5.6 4.15h.8V7.4h-.8zm0 4.05h.8v.9h-.8z" />
        </svg>
    );
}

export function DueDateCell({ due, overdue }) {
    if (!due) return null;
    if (overdue) {
        return (
            <span className={`${styles.dueOverdue} ${styles.dueOverdueCell}`} title="Overdue">
                {due}
                <OverdueMark />
            </span>
        );
    }
    return <span className={styles.dueDate}>{due}</span>;
}

export function PriorityMark({ value, suffix }) {
    const tone = value === 'High' ? styles.priorityHigh : value === 'Medium' ? styles.priorityMedium : styles.priorityLow;
    const dot = value === 'High' ? styles.dotHigh : value === 'Medium' ? styles.dotMedium : styles.dotLow;
    return (
        <span className={`${styles.priority} ${tone}`}>
            <span className={`${styles.dot} ${dot}`} />
            {value}
            {suffix ? ` ${suffix}` : ''}
        </span>
    );
}

export function RagMark({ value }) {
    if (!value) return <span className={styles.metaPlain}>—</span>;
    const key = String(value).toLowerCase();
    const tone = key === 'red' ? styles.ragRed : key === 'amber' ? styles.ragAmber : key === 'green' ? styles.ragGreen : styles.priorityLow;
    const dot = key === 'red' ? styles.dotRed : key === 'amber' ? styles.dotAmber : key === 'green' ? styles.dotGreen : styles.dotLow;
    return (
        <span className={`${styles.priority} ${tone}`}>
            <span className={`${styles.dot} ${dot}`} />
            {value}
        </span>
    );
}

const IRL_PRIORITY_CHOICES = ['High', 'Medium', 'Low'];

function HoverEditActions({ onSave, onCancel, busy, saveLabel, cancelLabel }) {
    return (
        <span className={styles.hoverEditActions}>
            <button type="button" className={styles.editSaveBtn} onClick={onSave} disabled={busy} aria-label={saveLabel}>
                <Checkmark16Regular />
            </button>
            <button type="button" className={styles.editCancelBtn} onClick={onCancel} aria-label={cancelLabel}>
                <Dismiss16Regular />
            </button>
        </span>
    );
}

function HoverEditFrame({ children }) {
    const [hot, setHot] = React.useState(false);
    return (
        <span
            className={`${styles.hoverEdit}${hot ? ` ${styles.hoverEditHot}` : ''}`}
            onMouseEnter={() => setHot(true)}
            onMouseLeave={() => setHot(false)}>
            {children}
        </span>
    );
}

function requestDueIso(item) {
    const raw = item?.dueDate || item?.due_date || '';
    if (typeof raw === 'string') {
        const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
        if (match) return match[1];
    }
    return '';
}

function priorityChoices(value) {
    if (value && !IRL_PRIORITY_CHOICES.includes(value)) {
        return [value, ...IRL_PRIORITY_CHOICES];
    }
    return IRL_PRIORITY_CHOICES;
}

export function EditableDueDate({ item, onSave, busy, appearance }) {
    const iso = requestDueIso(item);
    const overdue = isRequestOverdue(item);
    const editable = canEditRequestField(onSave);
    const [editing, setEditing] = React.useState(false);
    const [value, setValue] = React.useState(iso);

    const startEdit = () => {
        if (!editable) return;
        setValue(iso);
        setEditing(true);
    };

    const cancelEdit = () => {
        setValue(iso);
        setEditing(false);
    };

    const saveEdit = async () => {
        if (!editable || busy) return;
        if (value === iso) {
            setEditing(false);
            return;
        }
        const saved = await onSave({ due_date: value });
        if (saved !== false) setEditing(false);
    };

    const onKeyDown = event => {
        if (event.key === 'Escape') {
            event.preventDefault();
            cancelEdit();
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            saveEdit();
        }
    };

    if (editing) {
        return (
            <HoverEditFrame>
                <input
                    type="date"
                    className={styles.inlineDate}
                    value={value}
                    onChange={event => setValue(event.target.value)}
                    onKeyDown={onKeyDown}
                    autoFocus
                    disabled={busy}
                />
                <HoverEditActions onSave={saveEdit} onCancel={cancelEdit} busy={busy} saveLabel="Save due date" cancelLabel="Cancel due date" />
            </HoverEditFrame>
        );
    }

    const headerDue = (
        <span className={overdue ? `${styles.dueOverdue} ${styles.dueOverdueCell}` : styles.metaPlain}>
            Due {item?.due || '—'}
            {overdue ? ' · Overdue' : ''}
            {overdue ? <OverdueMark /> : null}
        </span>
    );
    const cellDue = item?.due ? <DueDateCell due={item.due} overdue={overdue} /> : <span className={styles.dueDate}>—</span>;
    const display = appearance === 'header' ? headerDue : cellDue;

    return (
        <HoverEditFrame>
            {display}
            {editable ? (
                <button type="button" className={styles.hoverEditBtn} onClick={startEdit} aria-label="Edit due date">
                    <Edit12Regular />
                </button>
            ) : null}
        </HoverEditFrame>
    );
}

export function EditablePriority({ item, onSave, busy }) {
    const current = item?.priority || 'None';
    const editable = canEditRequestField(onSave);
    const [editing, setEditing] = React.useState(false);
    const [value, setValue] = React.useState(current);

    const startEdit = () => {
        if (!editable) return;
        setValue(current);
        setEditing(true);
    };

    const cancelEdit = () => {
        setValue(current);
        setEditing(false);
    };

    const saveEdit = async () => {
        if (!editable || busy) return;
        if (value === current) {
            setEditing(false);
            return;
        }
        const saved = await onSave({ priority: value });
        if (saved !== false) setEditing(false);
    };

    const onKeyDown = event => {
        if (event.key === 'Escape') {
            event.preventDefault();
            cancelEdit();
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            saveEdit();
        }
    };

    if (editing) {
        return (
            <HoverEditFrame>
                <select
                    className={styles.inlineSelect}
                    value={value}
                    onChange={event => setValue(event.target.value)}
                    onKeyDown={onKeyDown}
                    autoFocus
                    disabled={busy}>
                    {priorityChoices(current).map(choice => (
                        <option key={choice} value={choice}>
                            {choice}
                        </option>
                    ))}
                </select>
                <HoverEditActions onSave={saveEdit} onCancel={cancelEdit} busy={busy} saveLabel="Save priority" cancelLabel="Cancel priority" />
            </HoverEditFrame>
        );
    }

    return (
        <HoverEditFrame>
            <PriorityMark value={current} />
            {editable ? (
                <button type="button" className={styles.hoverEditBtn} onClick={startEdit} aria-label="Edit priority">
                    <Edit12Regular />
                </button>
            ) : null}
        </HoverEditFrame>
    );
}

export function FreshnessBadge({ value, appearance }) {
    if (!value) return null;
    if (appearance === 'pill' || value === 'New evidence' || value === 'Potentially affected') {
        return <span className={`${styles.badge} ${styles.badgeNewEvidence}`}>{value}</span>;
    }
    return <span className={`${styles.badge} ${styles.badgeReviewRequired}`}>{value}</span>;
}

export function ModalShell({ width = 'default', children }) {
    const widthClass = width === 'form' ? styles.modalForm : width === 'narrow' ? styles.modalNarrow : width === 'small' ? styles.modalSmall : '';
    return (
        <div className={styles.overlay}>
            <div className={`${styles.modal} ${widthClass}`}>{children}</div>
        </div>
    );
}

export function WorkstreamOptions({ workstreams, valueKey = 'name' }) {
    const options = workstreams != null ? workstreams : WORKSTREAMS;
    return options.map(ws => (
        <option key={ws.id} value={ws[valueKey] ?? ws.name}>
            {ws.name}
        </option>
    ));
}
